import classNames from "classnames";
import React, {
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    AuditLogEntryFilters, AuditLogSeverity, GetAuditLogsListQuery, User, useGetAuditLogsListQuery,
} from "../../../../__generated__/api";
import { Loading } from "../../../../components";
import UserDisplay from "../../../../components/agent-v3/labels/user/User";
import CodeBlock from "../../../../components/code-block/CodeBlock";
import Confirm from "../../../../components/confirm/Confirm";
import { Filters as FiltersSelector } from "../../../../components/filters/Filters";
import DateRangeCalendarPicker from "../../../../components/pickers/date-range/DateRangeCalendarPicker";
import Button from "../../../../components/theme/button/Button";
import SortUpDown, { Direction } from "../../../../components/theme/sortable/SortBy";
import useArticleContext from "../../../../contexts/HelpContext";
import { Paths, useParameterFilters } from "../../../../contexts/Routing";
import { Time, parseFilterDates, tryParseDate } from "../../../../date";
import { ErrorRemote } from "../../../error/Error";
import Styles from "./AuditLogs.module.scss";

function getStartDate() {
    return new Date(new Date().getTime() - (Time.WEEK * 2));
}

const SEVERITY_TEXT = {
    [AuditLogSeverity.Trace]: "Trace",
    [AuditLogSeverity.Debug]: "Debug",
    [AuditLogSeverity.Info]: "Info",
    [AuditLogSeverity.Warn]: "Warn",
    [AuditLogSeverity.Error]: "Error",
    [AuditLogSeverity.Fatal]: "Fatal",
};

const SEVERITIES = Object.entries(SEVERITY_TEXT).map(([value, label]) => ({ value, label }));

type AuditLogEntryItem = GetAuditLogsListQuery["auditLogs"][0];

type AuditLogKey = keyof AuditLogEntryItem;

interface SortKey {
    key: AuditLogKey;
    direction: Direction;
}

function TableHeader({
    label,
    direction,
    keyName,
    onSortSet,
}: {
    label: string;
    direction: Direction | undefined;
    keyName: AuditLogKey;
    onSortSet: ((value: SortKey) => void);
}): ReactElement {
    return (
        <th>
            <div className="d-flex">
                <label className={Styles.header}>
                    {label}
                </label>
                <SortUpDown
                    direction={direction}
                    onChange={value => onSortSet({
                        key: keyName,
                        direction: value,
                    })}
                />
            </div>

        </th>
    );
}

const COLUMNS: {label: string; key: AuditLogKey}[] = [
    {
        label: "Timestamp",
        key: "timestamp",
    },
    {
        label: "Message",
        key: "message",
    },
    {
        label: "Actor",
        key: "actor",
    },
    {
        label: "Severity",
        key: "severity",
    },

];

interface Filters extends AuditLogEntryFilters {
    sortKey: AuditLogKey;
    sortDirection: Direction;
    actorIds: string[];
    workspaceIds: string[];
    severities: AuditLogSeverity[];
}

function AuditLog({ log, shouldShowWorkspace }: {log: AuditLogEntryItem; shouldShowWorkspace: boolean}): ReactElement {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <tr>
            {isExpanded && (
                <Confirm
                    shouldShow
                    size={log.differences ? "xl" : "lg"}
                    onHide={() => setIsExpanded(false)}
                    title={log.message.substring(0, 40)}
                >
                    <div className="row">
                        <div className={classNames(Styles["log-header"], "col-3")}>Timestamp</div>
                        <div className="col">{tryParseDate(log.timestamp)?.toLocaleString()}</div>
                    </div>
                    <div className="row">
                        <div className={classNames(Styles["log-header"], "col-3")}>Actor</div>
                        <div className="col">
                            <UserDisplay user={log.actor as User} allowOverlay={false} variant="xxs" />
                        </div>
                    </div>
                    <div className="row">
                        <div className={classNames(Styles["log-header"], "col-3")}>Message</div>
                        <div className="col">{log.message}</div>
                    </div>
                    <div className="row">
                        <div className={classNames(Styles["log-header"], "col-3")}>Severity</div>
                        <div className="col">{SEVERITY_TEXT[log.severity]}</div>
                    </div>
                    {shouldShowWorkspace && (
                        <div className="row">
                            <div className={classNames(Styles["log-header"], "col-3")}>Workspace</div>
                            <div className="col">{log.workspace?.name}</div>
                        </div>
                    )}

                    <div className="row">
                        <div className={classNames(Styles["log-header"], "col-3")}>IP Address</div>
                        <div className="col">{log.ipAddress}</div>
                    </div>

                    <div className="mt-4">
                        <h5>Properties</h5>
                        <CodeBlock code={JSON.stringify(log.attributes, null, 4)} />
                    </div>
                    {log.differences?.isNotEmpty && (
                        <div className="mt-4">
                            <h5>Changes</h5>
                            <table className={classNames("table w-100", Styles.table)}>
                                <thead>
                                    <tr>
                                        <th>Path</th>
                                        <th>Previous</th>
                                        <th>Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {log.differences.map(difference => (
                                        <tr key={difference.path.join(".")}>
                                            <td>{difference.path.join(".")}</td>
                                            <td>{difference.previousValue}</td>
                                            <td>{difference.updatedValue}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                </Confirm>
            )}
            <td>
                <Button
                    variant="outline-gray"
                    className="p3x-1 pxy-1 btn-sm"
                    size="small"
                    onClick={() => setIsExpanded(value => !value)}
                >
                    View
                </Button>
            </td>
            <td>
                {tryParseDate(log.timestamp)?.toLocaleString()}
            </td>
            <td>{log.message}</td>
            <td aria-label="actor">
                <UserDisplay user={log.actor as User} allowOverlay={false} variant="xxs" />
            </td>
            <td>{SEVERITY_TEXT[log.severity]}</td>
            {shouldShowWorkspace && (
                <td>{log.workspace?.name}</td>
            )}
        </tr>
    );
}
export default function AuditLogs(): ReactElement {
    useArticleContext("urn:fq:article:1ead24cac857484296a375b5400b74e3");

    const [filters, , setFilters] = useParameterFilters<Filters & Record<string, unknown>>(Paths.ADMINISTRATOR, {
        startDate: getStartDate(),
        sortKey: "timestamp",
        sortDirection: Direction.DESCENDING,
        actorIds: [],
        severities: [],
        workspaceIds: [],
    });

    const [sort, setSort] = useState<SortKey>({
        key: filters.sortKey,
        direction: filters.sortDirection,
    });

    useEffect(() => {
        setFilters({
            ...filters,
            sortKey: sort.key,
            sortDirection: sort.direction,
        });
    }, [sort]);

    const { data, loading: isLoading, error } = useGetAuditLogsListQuery({
        variables: {
            filters: {
                startDate: filters.startDate,
                endDate: filters.endDate,
            },
        },
    });

    const actors = useMemo(
        () => data?.auditLogs
            .map(row => row.actor)
            .filterNull()
            .dedupe(item => item?.id)
            .map(actor => ({
                value: actor.id,
                label: actor.name as string,
            })),
        [data?.auditLogs],
    );

    const workspaces = useMemo(
        () => data?.auditLogs
            .map(row => row.workspace)
            .filterNull()
            .dedupe(item => item?.installationId)
            .map(item => ({
                value: item.installationId,
                label: item.name as string,
            })),
        [data?.auditLogs],
    );

    if (isLoading) {
        return <Loading />;
    }
    if (error) {
        return <ErrorRemote error={error} />;
    }

    let sortedData = data?.auditLogs ? [...data.auditLogs] : [];
    if (sort) {
        const directionValue = sort.direction === Direction.ASCENDING ? 1 : -1;
        sortedData = sortedData.sort((left, right) => {
            const leftValue = left[sort.key];
            const rightValue = right[sort.key];

            let value = 0;
            if (sort.key === "timestamp") {
                const leftValueDate = tryParseDate(leftValue);
                const rightValueDate = tryParseDate(rightValue);
                value = (leftValueDate?.getTime() ?? 0) - (rightValueDate?.getTime() ?? 0);
            } else if (sort.key === "actor" || sort.key === "workspace") {
                value = ((leftValue as User).name ?? "").localeCompare((rightValue as User).name as string);
            } else if (typeof leftValue === "string" && typeof rightValue === "string") {
                value = leftValue.localeCompare(rightValue);
            }

            return directionValue * value;
        });
    }
    if (filters.actorIds?.isNotEmpty) {
        const set = new Set(filters.actorIds);
        sortedData = sortedData.filter(row => set.has(row.actor?.id));
    }
    if (filters.severities?.isNotEmpty) {
        const set = new Set(filters.severities);
        sortedData = sortedData.filter(row => set.has(row.severity));
    }
    if (filters.workspaceIds?.isNotEmpty) {
        const set = new Set(filters.workspaceIds);
        sortedData = sortedData.filter(row => set.has(row.workspace?.installationId));
    }

    const shouldShowWorkspace = workspaces != null && workspaces?.length > 1;

    return (
        <div className="d-flex align-items-stretch flex-column">
            <div className={`d-flex justify-content-between align-items-center
                        bg-white border-bottom border-gray-400 p-3 mt-3 mb-3 rounded`}
            >
                <h5 className="m-0">
                    Audit Logs
                </h5>
                <div className="d-flex flex-grow-1 align-items-center justify-content-end">
                    {shouldShowWorkspace && (
                        <FiltersSelector
                            toggleText="Workspace"
                            categories={workspaces}
                            searchMinimum={5}
                            className="text-light-400 p-0 m-0"
                            toggleClassName="btn-none text-light-400 p-0 me-4"
                            selectedCategories={
                                new Set(filters.workspaceIds)
                            }
                            onSelected={workspaceIds => setFilters({
                                ...filters,
                                workspaceIds: Array.from(workspaceIds),
                            })}
                        />
                    )}
                    <FiltersSelector
                        toggleText="Actor"
                        categories={actors}
                        searchMinimum={5}
                        className="text-light-400 p-0 m-0"
                        toggleClassName="btn-none text-light-400 p-0 me-4"
                        selectedCategories={
                            new Set(filters.actorIds)
                        }
                        onSelected={actorIds => setFilters({
                            ...filters,
                            actorIds: Array.from(actorIds),
                        })}
                    />
                    <FiltersSelector
                        toggleText="Severity"
                        categories={SEVERITIES}
                        className="text-light-400 p-0 m-0"
                        toggleClassName="btn-none text-light-400 p-0 me-4"
                        selectedCategories={
                            new Set(filters.severities)
                        }
                        onSelected={severities => setFilters({
                            ...filters,
                            severities: Array.from(severities) as AuditLogSeverity[],
                        })}
                    />
                    <DateRangeCalendarPicker
                        shouldShowIcon={false}
                        dropdownClassname="w-auto border-0"
                        toggleClassName="btn-none text-light-400 p-0 me-4"
                        toggleText="Created"
                        className="text-light-400 p-0 m-0 shadow"
                        value={{
                            startDate: filters.startDate,
                            endDate: filters.endDate,
                        }}
                        onChange={change => {
                            const dates = parseFilterDates(change);
                            setFilters({
                                ...filters,
                                startDate: dates.startDate ? new Date(dates.startDate) : undefined,
                                endDate: dates.endDate ? new Date(dates.endDate) : undefined,
                            });
                        }}
                        // onReset={() => setFilters({ ...filters, startDate: getStartDate(), endDate: undefined })}
                    />

                </div>
            </div>
            <div className="border p-3 rounded bg-white w-100">

                <table className={classNames("table w-100", Styles.table)}>
                    <thead>
                        <tr>
                            <th aria-label="Text" />
                            {COLUMNS.map(({ label, key }) => (
                                <TableHeader
                                    label={label}
                                    direction={sort.key === key ? sort.direction : undefined}
                                    key={key}
                                    keyName={key}
                                    onSortSet={setSort}
                                />
                            ))}
                            {shouldShowWorkspace && (
                                <TableHeader
                                    label="Workspace"
                                    direction={sort.key === "workspace" ? sort.direction : undefined}
                                    keyName="workspace"
                                    onSortSet={setSort}
                                />
                            )}

                        </tr>
                    </thead>
                    <tbody>
                        {sortedData?.map(log => (
                            <AuditLog key={log.id} log={log} shouldShowWorkspace={shouldShowWorkspace} />
                        ))}
                    </tbody>
                </table>

            </div>

        </div>
    );
}
